import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Type, X } from 'lucide-react';
import {
  getOpenOsV2FutureDateBounds,
  installOpenOsV2SearchBridge,
  uninstallOpenOsV2SearchBridge,
} from '../utils/openOsV2SearchBridge';
import {
  installOpenOsV2HistoryBridge,
  uninstallOpenOsV2HistoryBridge,
} from '../utils/openOsV2HistoryBridge';
import OpenOsV2ProductionChart from './OpenOsV2ProductionChart';
import OpenOsV2OryaTextareaAssist from './OpenOsV2OryaTextareaAssist';
import OpenOsV2App from '../open-os-v2/OpenOsV2App';
import { PeriodModal } from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { useComposerVoiceRecorder } from '../../board-chat/components/ComposerVoiceRecorder';
import { BoardChatAiWriteMenu } from '../../board-chat/components/BoardChatAiWriteMenu';
import '../../board-chat/components/BoardChatPanel.css';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';
import './OpenOsV2MockOverlay.css';

// Isola o app do mock das mudanças de estado do overlay (tooltip, gravação, etc.)
const MemoOpenOsV2App = React.memo(OpenOsV2App);

/**
 * Overlay temporário: mock Abrir OS v2 no mesmo DOM (Select element)
 * com fundo transparente — elementos flutuam sobre o Board.
 */
export default function OpenOsV2MockOverlay({
  isOpen,
  onClose,
  onOSCreated,
  onViewOs,
  goIdProduto = null,
  agendaOnly = false,
  onAgendaConfirm,
  excludeTechnicianName = '',
  scheduleOriginAddress = '',
  scheduleOriginCity = '',
  hideApenasTecnico = false,
  agendaConfirming = false,
  agendaDialogTitle = 'Programar atendimento',
  overlayClassName = '',
  overlayZIndex = null,
  restrictOpenOsByUserTypes = false,
  userOsTypesLoad = null,
}) {
  const stackZIndex = overlayZIndex == null ? 20050 : Number(overlayZIndex);
  const hostRef = useRef(null);
  const [prodChartHost, setProdChartHost] = useState(null);
  const [prodChartData, setProdChartData] = useState(null);
  const [cellTip, setCellTip] = useState(null);
  const [futureDatePicker, setFutureDatePicker] = useState(null);
  const [agendaVoiceError, setAgendaVoiceError] = useState('');
  const [agendaVoiceAnchor, setAgendaVoiceAnchor] = useState(null);
  const agendaVoice = useComposerVoiceRecorder({
    onError: (msg) => setAgendaVoiceError(msg || 'Falha no microfone'),
  });
  const agendaVoiceRef = useRef(agendaVoice);
  agendaVoiceRef.current = agendaVoice;

  const [obsVoiceError, setObsVoiceError] = useState('');
  const [obsVoiceAnchor, setObsVoiceAnchor] = useState(null);
  const [obsAiOpen, setObsAiOpen] = useState(false);
  const [obsAiAnchor, setObsAiAnchor] = useState(null);
  const obsVoice = useComposerVoiceRecorder({
    onError: (msg) => setObsVoiceError(msg || 'Falha no microfone'),
  });
  const obsVoiceRef = useRef(obsVoice);
  obsVoiceRef.current = obsVoice;

  const bridgesRef = useRef(false);
  const prodChartKeyRef = useRef('');

  // OpenOsV2App lê os bridges já no primeiro render/componentDidMount,
  // então eles precisam existir antes do efeito do overlay rodar.
  if (isOpen && !bridgesRef.current) {
    bridgesRef.current = true;
    installOpenOsV2SearchBridge();
    installOpenOsV2HistoryBridge();
  } else if (!isOpen && bridgesRef.current) {
    bridgesRef.current = false;
  }

  const inHost = (el) => !!el && !!hostRef.current && hostRef.current.contains(el);

  const stopAgendaVoice = () => {
    agendaVoiceRef.current.cancel();
    setAgendaVoiceError('');
    setAgendaVoiceAnchor(null);
  };

  const ensureMicPermission = async () => {
    try {
      if (navigator?.permissions?.query) {
        const st = await navigator.permissions.query({ name: 'microphone' });
        if (st.state === 'granted') return true;
        if (st.state === 'denied') return false;
      }
    } catch {
      /* Permissions API sem suporte a 'microphone' */
    }
    if (!navigator?.mediaDevices?.getUserMedia) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch {
      return false;
    }
  };

  const stopObsVoice = () => {
    obsVoiceRef.current.cancel();
    setObsVoiceError('');
    setObsVoiceAnchor(null);
  };

  const closeObsAi = () => {
    setObsAiOpen(false);
    setObsAiAnchor(null);
  };

  useEffect(() => {
    if (!isOpen) {
      setProdChartHost(null);
      setProdChartData(null);
      setCellTip(null);
      setFutureDatePicker(null);
      stopAgendaVoice();
      stopObsVoice();
      closeObsAi();
      window.__openOsV2SetProductionChart = undefined;
      window.__openOsV2OpenFutureDatePicker = undefined;
      window.__openOsV2CloseFutureDatePicker = undefined;
      window.__openOsV2SetFutureDate = undefined;
      window.__openOsV2AppendStandbyNotes = undefined;
      window.__openOsV2CancelAgendaVoice = undefined;
      window.__openOsV2GetObs = undefined;
      window.__openOsV2SetObs = undefined;
      window.__openOsV2AppendObs = undefined;
      uninstallOpenOsV2SearchBridge();
      uninstallOpenOsV2HistoryBridge();
      return undefined;
    }

    let cancelled = false;
    installOpenOsV2SearchBridge();
    installOpenOsV2HistoryBridge();

    window.__openOsV2SetProductionChart = (payload) => {
      queueMicrotask(() => {
        if (cancelled) return;
        if (!payload || payload.clear) {
          prodChartKeyRef.current = '';
          setProdChartData(null);
          return;
        }
        const cicloRaw = payload.cicloMensal;
        const cicloN = cicloRaw != null && cicloRaw !== '' ? Math.trunc(Number(cicloRaw)) : null;
        const next = {
          meses: Array.isArray(payload.meses) ? payload.meses : [],
          series: Array.isArray(payload.series) ? payload.series : [],
          activeKeys: Array.isArray(payload.activeKeys) ? payload.activeKeys : [],
          cicloMensal: Number.isFinite(cicloN) && cicloN > 0 ? cicloN : null,
        };
        // O app republica o payload a cada render; sem isso o estado oscilaria em loop.
        const key = JSON.stringify(next);
        if (key === prodChartKeyRef.current) return;
        prodChartKeyRef.current = key;
        setProdChartData(next);
      });
    };

    window.__openOsV2OpenFutureDatePicker = (payload) => {
      if (cancelled) return;
      setFutureDatePicker(payload || null);
    };
    window.__openOsV2CloseFutureDatePicker = () => {
      if (cancelled) return;
      setFutureDatePicker(null);
    };
    window.__openOsV2CancelAgendaVoice = () => {
      if (cancelled) return;
      stopAgendaVoice();
    };

    const syncProdHost = () => {
      if (cancelled) return;
      const el = document.getElementById('oosv2-prod-recharts-host');
      setProdChartHost((prev) => (prev === el ? prev : el || null));
    };
    const mo = new MutationObserver(syncProdHost);
    mo.observe(document.body, { childList: true, subtree: true });
    syncProdHost();

    return () => {
      cancelled = true;
      mo.disconnect();
      window.__openOsV2SetProductionChart = undefined;
      window.__openOsV2OpenFutureDatePicker = undefined;
      window.__openOsV2CloseFutureDatePicker = undefined;
      window.__openOsV2SetFutureDate = undefined;
      window.__openOsV2AppendStandbyNotes = undefined;
      window.__openOsV2CancelAgendaVoice = undefined;
      window.__openOsV2GetObs = undefined;
      window.__openOsV2SetObs = undefined;
      window.__openOsV2AppendObs = undefined;
      setProdChartHost(null);
      setProdChartData(null);
      setCellTip(null);
      setFutureDatePicker(null);
      stopAgendaVoice();
      stopObsVoice();
      closeObsAi();
      uninstallOpenOsV2SearchBridge();
      uninstallOpenOsV2HistoryBridge();
    };
  }, [isOpen]);

  // Fechar pelo Descartar / × do próprio mock (fora do dialog de agenda)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const obsVoiceBtn = t.closest('[data-oosv2-obs-voice]');
      if (obsVoiceBtn && inHost(obsVoiceBtn)) {
        e.preventDefault();
        e.stopPropagation();
        const rec = obsVoiceRef.current;
        if (rec.busy) return;
        closeObsAi();
        stopAgendaVoice();
        setObsVoiceError('');
        if (rec.recording) {
          rec.toggle();
          setObsVoiceAnchor(null);
          return;
        }
        const wrap = obsVoiceBtn.closest('.oosv2-obs-body') || obsVoiceBtn;
        const r = wrap.getBoundingClientRect();
        setObsVoiceAnchor({
          top: r.bottom + 6,
          left: Math.max(12, r.right - 268),
        });
        ensureMicPermission().then((ok) => {
          if (!ok) {
            setObsVoiceError('Permita o acesso ao microfone no navegador para usar a fala para texto.');
            return;
          }
          obsVoiceRef.current.toggle();
        });
        return;
      }
      const voiceBtn = t.closest('[data-oosv2-agenda-voice]');
      if (voiceBtn && inHost(voiceBtn)) {
        e.preventDefault();
        e.stopPropagation();
        const rec = agendaVoiceRef.current;
        if (rec.busy) return;
        closeObsAi();
        stopObsVoice();
        setAgendaVoiceError('');
        if (rec.recording) {
          rec.toggle();
          setAgendaVoiceAnchor(null);
          return;
        }
        const wrap = voiceBtn.closest('.oosv2-agenda-notes-wrap');
        const r = (wrap || voiceBtn).getBoundingClientRect();
        setAgendaVoiceAnchor({
          top: r.bottom + 6,
          left: Math.max(12, r.right - 268),
        });
        rec.toggle();
        return;
      }
      if (!t.closest('.oosv2-ultimo-obs-wrap') && !t.closest('.oosv2-ultimo-obs-tip')) {
        const openObs = document.querySelector('.oosv2-ultimo-obs-btn.is-on');
        if (openObs && inHost(openObs)) {
          openObs.click();
        }
      }
      const dateBtn = t.closest('.oosv2-agenda-date-btn');
      if (dateBtn && inHost(dateBtn)) {
        if (dateBtn.classList.contains('is-disabled')) return;
        e.preventDefault();
        e.stopPropagation();
        const r = dateBtn.getBoundingClientRect();
        const bounds = getOpenOsV2FutureDateBounds();
        const current = String(dateBtn.textContent || '').trim();
        const currentIso = /^\d{2}\/\d{2}\/\d{4}$/.test(current)
          ? current.split('/').reverse().join('-')
          : null;
        const validCurrent = currentIso && currentIso >= bounds.minDate ? currentIso : null;
        setFutureDatePicker({
          anchorRect: {
            top: r.top,
            left: r.left,
            bottom: r.bottom,
            right: r.right,
            width: r.width,
            height: r.height,
          },
          currentDate: validCurrent,
          minDate: bounds.minDate,
          maxDate: bounds.maxDate,
        });
        return;
      }
      const btn = t.closest('button');
      if (!btn || !inHost(btn)) return;
      if (btn.closest('.oosv2-agenda-dialog')) return;
      if (btn.closest('.oosv2-hist-close') || btn.closest('.oosv2-hist-header')) return;
      if (btn.closest('.oosv2-lista-modal')) return;
      if (btn.closest('.oosv2-flow-close')) onClose?.();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onMouseDown = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const aiBtn = t.closest('[data-oosv2-obs-ai]');
      if (!aiBtn || !inHost(aiBtn)) return;
      e.preventDefault();
      e.stopPropagation();
      stopObsVoice();
      const r = aiBtn.getBoundingClientRect();
      setObsAiAnchor({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
      setObsAiOpen((v) => !v);
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [isOpen]);

  useEffect(() => {
    const on = agendaVoice.recording || agendaVoice.busy;
    document.querySelectorAll('[data-oosv2-agenda-voice]').forEach((el) => {
      el.classList.toggle('is-recording', on);
    });
  }, [agendaVoice.recording, agendaVoice.busy]);

  useEffect(() => {
    const on = obsVoice.recording || obsVoice.busy;
    document.querySelectorAll('[data-oosv2-obs-voice]').forEach((el) => {
      el.classList.toggle('is-recording', on);
    });
  }, [obsVoice.recording, obsVoice.busy]);

  useEffect(() => {
    document.querySelectorAll('[data-oosv2-obs-ai]').forEach((el) => {
      el.classList.toggle('is-on', obsAiOpen);
    });
  }, [obsAiOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const hide = () => setCellTip(null);

    const onOver = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const el = t.closest('.oosv2-ellip');
      if (!el || !el.closest('.open-os-v2-mock-host')) {
        hide();
        return;
      }
      if (el.classList.contains('oosv2-hist-tipo-badge')) {
        hide();
        return;
      }
      if (el.scrollWidth <= el.clientWidth + 1) {
        hide();
        return;
      }
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || text === '—') {
        hide();
        return;
      }
      const r = el.getBoundingClientRect();
      const tipW = Math.min(320, window.innerWidth - 24);
      let left = r.left + r.width / 2;
      left = Math.max(tipW / 2 + 8, Math.min(left, window.innerWidth - tipW / 2 - 8));
      const placeBelow = r.top < 48;
      setCellTip({
        text,
        top: placeBelow ? r.bottom + 8 : Math.max(8, r.top - 8),
        left,
        placeBelow,
      });
    };

    const onOut = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const el = t.closest('.oosv2-ellip');
      if (!el) return;
      const rel = e.relatedTarget;
      if (rel instanceof Node && el.contains(rel)) return;
      hide();
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('scroll', hide, true);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const overlayClass = ['open-os-v2-mock-overlay', overlayClassName].filter(Boolean).join(' ');

  return createPortal(
    <div
      className={overlayClass}
      style={overlayZIndex != null ? { zIndex: stackZIndex } : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={agendaOnly ? 'Programar atendimento' : 'Abrir OS v2 (mock)'}
    >
      <div ref={hostRef} className="open-os-v2-mock-host" data-open-os-v2-host="1">
        <MemoOpenOsV2App
          onClose={onClose}
          onOSCreated={onOSCreated}
          onViewOs={onViewOs}
          goIdProduto={goIdProduto}
          agendaOnly={agendaOnly}
          onAgendaConfirm={onAgendaConfirm}
          excludeTechnicianName={excludeTechnicianName}
          scheduleOriginAddress={scheduleOriginAddress}
          scheduleOriginCity={scheduleOriginCity}
          hideApenasTecnico={hideApenasTecnico}
          agendaConfirming={agendaConfirming}
          agendaDialogTitle={agendaDialogTitle}
          restrictOpenOsByUserTypes={restrictOpenOsByUserTypes}
          userOsTypesLoad={userOsTypesLoad}
        />
      </div>
      {cellTip ? (
        <div
          className={'oosv2-table-tip' + (cellTip.placeBelow ? ' is-below' : '')}
          style={{ top: cellTip.top, left: cellTip.left }}
          role="tooltip"
        >
          {cellTip.text}
        </div>
      ) : null}
      {prodChartHost && prodChartData
        ? createPortal(
            <OpenOsV2ProductionChart
              meses={prodChartData.meses}
              series={prodChartData.series}
              activeKeys={prodChartData.activeKeys}
              cicloMensal={prodChartData.cicloMensal}
            />,
            prodChartHost,
          )
        : null}
      {futureDatePicker ? (
        <PeriodModal
          key={futureDatePicker.currentDate || futureDatePicker.minDate || 'oosv2-future-date'}
          anchorRect={futureDatePicker.anchorRect}
          currentPeriod={{
            startDate: futureDatePicker.currentDate || null,
            endDate: futureDatePicker.currentDate || null,
          }}
          minDate={futureDatePicker.minDate}
          maxDate={futureDatePicker.maxDate}
          initialMonthDate={futureDatePicker.minDate}
          title="Previsão de atendimento"
          hideHeader
          singleMonth
          dayMode
          hideActionsBorder
          useUserFormActions
          modalClassName="oosv2-future-date-modal tpm-track-date-modal ec-module-popover"
          zIndex={stackZIndex + 100}
          onApply={(start) => {
            if (start && futureDatePicker.minDate && start < futureDatePicker.minDate) return;
            if (typeof window.__openOsV2SetFutureDate === 'function') {
              window.__openOsV2SetFutureDate(start);
            }
            setFutureDatePicker(null);
          }}
          onClear={() => {
            if (typeof window.__openOsV2SetFutureDate === 'function') {
              window.__openOsV2SetFutureDate('');
            }
            setFutureDatePicker(null);
          }}
          onClose={() => setFutureDatePicker(null)}
        />
      ) : null}
      {agendaVoice.recording || agendaVoice.busy || agendaVoiceError ? (
        <div
          className="oosv2-agenda-voice-pop"
          style={
            agendaVoiceAnchor
              ? { top: `${agendaVoiceAnchor.top}px`, left: `${agendaVoiceAnchor.left}px` }
              : undefined
          }
          role="status"
          aria-live="polite"
        >
          {agendaVoice.recording || agendaVoice.busy ? (
            <>
              <span className={`oosv2-agenda-voice-pulse${agendaVoice.busy ? ' is-busy' : ''}`} aria-hidden />
              <span className="oosv2-agenda-voice-timer">
                {`${Math.floor((agendaVoice.elapsedSec || 0) / 60)}:${String((agendaVoice.elapsedSec || 0) % 60).padStart(2, '0')}`}
              </span>
              <span className="oosv2-agenda-voice-label">
                {agendaVoice.busy ? 'Processando…' : 'Gravando…'}
              </span>
              <button
                type="button"
                className="oosv2-agenda-voice-transcribe"
                disabled={agendaVoice.busy}
                onClick={async () => {
                  setAgendaVoiceError('');
                  try {
                    const rec = agendaVoiceRef.current;
                    if (!rec.speechSupported) {
                      await rec.cancel();
                      setAgendaVoiceError('Transcrição não suportada neste navegador.');
                      return;
                    }
                    const text = String((await rec.finishTranscript()) || '').trim();
                    if (!text) {
                      setAgendaVoiceError('Não foi possível obter o texto desta gravação.');
                      return;
                    }
                    if (typeof window.__openOsV2AppendStandbyNotes === 'function') {
                      window.__openOsV2AppendStandbyNotes(text);
                    }
                    setAgendaVoiceAnchor(null);
                  } catch (err) {
                    setAgendaVoiceError(err.message || 'Não foi possível obter o texto desta gravação.');
                  }
                }}
              >
                <Type size={13} strokeWidth={1.9} />
                Transcrever
              </button>
              <button
                type="button"
                className="oosv2-agenda-voice-cancel"
                disabled={agendaVoice.busy}
                aria-label="Cancelar gravação"
                onClick={() => stopAgendaVoice()}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </>
          ) : null}
          {agendaVoiceError ? <div className="oosv2-agenda-voice-error">{agendaVoiceError}</div> : null}
        </div>
      ) : null}
      {obsVoice.recording || obsVoice.busy || obsVoiceError ? (
        <div
          className="oosv2-agenda-voice-pop"
          style={
            obsVoiceAnchor
              ? { top: `${obsVoiceAnchor.top}px`, left: `${obsVoiceAnchor.left}px` }
              : undefined
          }
          role="status"
          aria-live="polite"
        >
          {obsVoice.recording || obsVoice.busy ? (
            <>
              <span className={`oosv2-agenda-voice-pulse${obsVoice.busy ? ' is-busy' : ''}`} aria-hidden />
              <span className="oosv2-agenda-voice-timer">
                {`${Math.floor((obsVoice.elapsedSec || 0) / 60)}:${String((obsVoice.elapsedSec || 0) % 60).padStart(2, '0')}`}
              </span>
              <span className="oosv2-agenda-voice-label">
                {obsVoice.busy ? 'Processando…' : 'Gravando…'}
              </span>
              <button
                type="button"
                className="oosv2-agenda-voice-transcribe"
                disabled={obsVoice.busy}
                onClick={async () => {
                  setObsVoiceError('');
                  try {
                    const rec = obsVoiceRef.current;
                    if (!rec.speechSupported) {
                      await rec.cancel();
                      setObsVoiceError('Transcrição não suportada neste navegador.');
                      return;
                    }
                    const text = String((await rec.finishTranscript()) || '').trim();
                    if (!text) {
                      setObsVoiceError('Não foi possível obter o texto desta gravação.');
                      return;
                    }
                    if (typeof window.__openOsV2AppendObs === 'function') {
                      window.__openOsV2AppendObs(text);
                    }
                    setObsVoiceAnchor(null);
                  } catch (err) {
                    setObsVoiceError(err.message || 'Não foi possível obter o texto desta gravação.');
                  }
                }}
              >
                <Type size={13} strokeWidth={1.9} />
                Transcrever
              </button>
              <button
                type="button"
                className="oosv2-agenda-voice-cancel"
                disabled={obsVoice.busy}
                aria-label="Cancelar gravação"
                onClick={() => stopObsVoice()}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </>
          ) : null}
          {obsVoiceError ? <div className="oosv2-agenda-voice-error">{obsVoiceError}</div> : null}
        </div>
      ) : null}
      <OpenOsV2OryaTextareaAssist hostRef={hostRef} active={isOpen} />
      {obsAiOpen && obsAiAnchor ? (
        <div
          className="oosv2-obs-ai-anchor"
          style={{
            top: `${obsAiAnchor.top}px`,
            left: `${obsAiAnchor.left}px`,
            width: `${obsAiAnchor.width}px`,
            height: `${obsAiAnchor.height}px`,
          }}
        >
          <BoardChatAiWriteMenu
            open
            hideTrigger
            draft={typeof window.__openOsV2GetObs === 'function' ? window.__openOsV2GetObs() : ''}
            applyLabel="Usar nas observações"
            onOpenChange={(next) => {
              if (!next) closeObsAi();
            }}
            onApply={(text) => {
              const next = String(text || '').trim();
              if (next && typeof window.__openOsV2SetObs === 'function') {
                window.__openOsV2SetObs(next);
              }
              closeObsAi();
            }}
          />
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
